/**
 * This file is made for formatting data from the OneSearch request to a format our propertyCard expects
 */

import { formatPropertyPageURL } from '@awaze/mfe-common.utils.pdp-url'
import type { ImageProps } from '@awaze/design-system.compositions.property-card-composite'
import {
  Deposit,
  getActiveDepositScheme,
} from '@awaze/mfe-common.configs.brand'
import { AccommodationType } from '@awaze/mfe-common.types-and-mocks.site-search-service'

import {
  IMAGE_DOMAIN_NOVASOL,
  IMAGE_DOMAIN_BASE_URL,
  DEFAULT_IMAGE_URL,
  PRICE_FROM_NIGHTS,
} from 'src/constants'
import type {
  PropertyImageOSProps,
  SetActiveDepositSchemeProps,
  PropertyDetailLinkProps,
  PropertyFormatterProps,
  Brand,
  PropertyFormatterReturnProps,
} from './propertyFormatter.types'
import { removeFloorplanImagesFromImageArray } from './helpers'
import { isNovasolPoweredBrand } from '../../utils/isNovasolPoweredBrand'

export const getDecodedText = (text: string | undefined): string | null => {
  if (text === undefined || !text) return null

  const parser = new DOMParser()
  const doc = parser.parseFromString(text, 'text/html')
  const returnString = doc.documentElement.textContent

  if (returnString === null) return null

  return returnString
}

export const getParsedNumber = (
  value: string | number | undefined,
): number | null => {
  if (value === undefined || !value) return null
  const parsedNumber = parseFloat(value.toString())
  if (isNaN(parsedNumber)) {
    return null
  }
  return parsedNumber
}

export const getFormattedDate = (date: string | undefined): Date | null => {
  if (date === undefined || !date) return null

  const dateInstance = new Date(date)

  if (isNaN(dateInstance.getTime())) return null

  return dateInstance
}

export const setImageAttributes = (
  images: PropertyImageOSProps[] | undefined,
  brand: Brand,
): ImageProps[] => {
  if (images === undefined || images.length == 0)
    return [
      {
        imageURL: DEFAULT_IMAGE_URL,
        imageAlt: getDecodedText('Image coming soon') ?? '',
      },
    ]

  const imagesWithoutFloorplans = images
    ? removeFloorplanImagesFromImageArray(images)
    : []

  const formattedImageArray = imagesWithoutFloorplans.map(
    (image: PropertyImageOSProps) => {
      let imageURI

      if (isNovasolPoweredBrand(brand)) {
        const propertySuffix = image.assetId.split('/')[1]
        imageURI = `${IMAGE_DOMAIN_NOVASOL}/pic/600/${propertySuffix.substring(0, 3)}/${propertySuffix}`
      } else {
        imageURI = `${IMAGE_DOMAIN_BASE_URL}/${image.assetId.substring(0, 3)}/640/${image.assetId}.jpg`
      }

      return {
        imageURL: imageURI,
        imageAlt: getDecodedText(image.caption) ?? image.caption,
      }
    },
  )

  return formattedImageArray
}

export const getArrayValues = <T>(
  values: Array<T> | undefined,
): Array<T> | null => {
  if (!values?.length) return null

  return values
}

export const setActiveDepositScheme = ({
  cardinality,
  price,
  priceFrom,
  sleeps,
  startDate,
  accommodationType,
  deposits,
}: SetActiveDepositSchemeProps): Deposit | null => {
  if (startDate === null || deposits === null) return null

  let priceForActiveDepositScheme

  if (cardinality === 'SINGLE' && price) {
    priceForActiveDepositScheme = price
  } else {
    priceForActiveDepositScheme = priceFrom
  }

  const sleepsForActiveDepositScheme =
    cardinality === 'SINGLE' && sleeps ? sleeps : 0

  const dateToday = new Date()

  const startDateInst = new Date(startDate)

  const activeDepositScheme =
    startDate &&
    getActiveDepositScheme(
      deposits,
      dateToday,
      startDateInst,
      sleepsForActiveDepositScheme,
      priceForActiveDepositScheme ?? 0,
      accommodationType,
    )

  if (activeDepositScheme) {
    return activeDepositScheme
  } else {
    return null
  }
}

export const setPropertyDetailLink = ({
  lengthOfStay,
  title,
  code,
  siteName,
  siteCode,
  pdpUrlSegment,
  searchParameters,
}: PropertyDetailLinkProps): string => {
  lengthOfStay && searchParameters.set('nights', lengthOfStay.toString())

  const formattedParams = searchParameters.toString().replace(/%21/g, '') // HACK: Remove ! (ideally should be fixed in fusion)

  const pageURL = formatPropertyPageURL(
    pdpUrlSegment,
    title || '',
    code,
    formattedParams,
    siteName,
    siteCode,
  )

  return pageURL
}

const setExperimentsValues = ({
  formattedProps,
  searchParams,
  datedSearch,
  brand,
}: {
  formattedProps: PropertyFormatterReturnProps
  searchParams: URLSearchParams
  datedSearch: boolean
  brand: Brand
}) => {
  if (!datedSearch && brand !== 'novasol') {
    formattedProps.wasPrice = null
    formattedProps.startDate = null
  }

  if (
    searchParams.get('accommodationType') === 'holiday-parks' ||
    searchParams.get('accommodationType') === 'lodges'
  ) {
    formattedProps.startDate = null
  }

  return formattedProps
}

export const formatter = ({
  property,
  brand,
  deposits,
  searchParams,
  datedSearch,
  pdpUrlSegment,
}: PropertyFormatterProps): PropertyFormatterReturnProps => {
  const accommodationType = searchParams.get('accommodationType') || 'accom'

  const formattedPropertyCardProps: PropertyFormatterReturnProps = {
    code: property.code,
    title: getDecodedText(property.displayName),
    location: getDecodedText(property.location),
    images: setImageAttributes(property.images, brand),
    price: getParsedNumber(property.price),
    priceFrom: getParsedNumber(property.priceFrom),
    wasPrice: getParsedNumber(property.wasPrice),
    nights: getParsedNumber(property.lengthOfStay ?? PRICE_FROM_NIGHTS),
    pageURL: setPropertyDetailLink({
      startDate: getFormattedDate(property.startDate),
      lengthOfStay: getParsedNumber(property.lengthOfStay),
      title: getDecodedText(property.displayName),
      code: property.code,
      siteName: '',
      siteCode: '',
      pdpUrlSegment: pdpUrlSegment,
      searchParameters: searchParams,
    }),
    hasPetsFree: property.petsGoFree ?? false,
    hasCallout: property.isCustomerChoice ?? false,
    activeDepositScheme: setActiveDepositScheme({
      cardinality: property.cardinality,
      price: getParsedNumber(property.price),
      priceFrom: getParsedNumber(property.priceFrom),
      sleeps: getParsedNumber(property.property?.sleeps),
      deposits: deposits || null,
      startDate: datedSearch ? getFormattedDate(property.startDate) : null,
      accommodationType: accommodationType as AccommodationType,
    }),
    USPs: getArrayValues(property.USPs),
    includedExtras: getArrayValues(property.property?.includedExtras),
    rhs3: getDecodedText(property.rhs3),
    startDate: getFormattedDate(property.startDate),
    guests: getParsedNumber(property.property?.sleeps),
    bedrooms: getParsedNumber(property.property?.bedrooms),
    bathrooms: getParsedNumber(property.property?.bathrooms),
    pets: getParsedNumber(property.property?.pets),
    score: getParsedNumber(property.averageReviewScore),
    features: property.features || [],
    grade: getParsedNumber(property.grade),
    urgencyNudgeMessaging: property.urgencyNudgeMessaging ?? false,
    withFooter: true,
    cardinality: property.cardinality,
    showPropertySpecLabels: false,
    imageConfig: {
      eagerLoading: false,
      fetchPriority: 'auto',
      priority: false,
    },
  }

  const returnedProperty = setExperimentsValues({
    formattedProps: formattedPropertyCardProps,
    searchParams: searchParams,
    datedSearch: datedSearch,
    brand: brand,
  })

  return returnedProperty
}
